import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import axios from "axios";
import { useSession } from "@/services/auth0/session";
import * as z from "zod";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Billing } from "@/api.types";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { AutocompleteAddress } from "@/components/address-auto-complete";
import { logger } from "@/services/logger";
import { Card, CardContent } from "@/components/ui/card";
import { Info } from "lucide-react";

const billingSchema = z.object({
  line1: z.string().min(1, "Address line 1 is required"),
  line2: z.string().optional(),
  city: z.string().min(1, "City is required"),
  province: z.string().min(1, "State/Province is required"),
  country: z.string().min(1, "Country is required"),
  postalCode: z.string().min(1, "Postal code is required"),
  location: z.object({
    lat: z.number(),
    lng: z.number(),
  }),
  plusCode: z.string().optional(),
});

export default function BillingScreen() {
  const { token, customer } = useSession();
  const queryClient = useQueryClient();

  const form = useForm<z.infer<typeof billingSchema>>({
    resolver: zodResolver(billingSchema),
    defaultValues: {
      line1: "",
      line2: "",
      city: "",
      province: "",
      country: "",
      postalCode: "",
      location: { lat: 0, lng: 0 },
    },
    values: customer?.billing?.address
      ? {
          line1: customer.billing.address.line1,
          line2: customer.billing.address.line2 ?? "",
          city: customer.billing.address.city,
          province: customer.billing.address.province,
          country: customer.billing.address.country,
          postalCode: customer.billing.address.postalCode ?? "",
          location: customer.billing.address.location,
        }
      : undefined,
  });

  const createBillingMutation = useMutation({
    mutationFn: async (values: z.infer<typeof billingSchema>) => {
      if (!customer?.id) throw new Error("No customer ID found");

      const { data } = await axios.post<Billing>(
        `${import.meta.env.VITE_API_URL}/api/v1/customers/${customer.id}/billing`,
        values,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );
      return data;
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: ["customer", customer?.id],
      });
    },
  });

  const updateBillingMutation = useMutation({
    mutationFn: async (values: z.infer<typeof billingSchema>) => {
      if (!customer?.id) throw new Error("No customer ID found");

      const { data } = await axios.patch<Billing>(
        `${import.meta.env.VITE_API_URL}/api/v1/customers/${customer.id}/billing`,
        values,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );
      return data;
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: ["customer", customer?.id],
      });
    },
  });

  const onSubmit = form.handleSubmit((values) => {
    if (customer?.billing) {
      updateBillingMutation.mutate(values, {
        onError: (error) => {
          logger.error("Failed to update billing:", error);
        },
      });
    } else {
      createBillingMutation.mutate(values, {
        onError: (error) => {
          logger.error("Failed to create billing:", error);
        },
      });
    }
  });

  return (
    <div className="w-full max-w-xl space-y-8">
      <div>
        <h2 className="text-2xl font-semibold">Billing Information</h2>
        <p className="text-sm text-muted-foreground">
          {customer?.billing
            ? "Update your billing details and address"
            : "Set up your billing information"}
        </p>
      </div>

      <Card className="bg-gray-50">
        <CardContent className="pt-6">
          <div className="flex flex-col items-start gap-2">
            <div className="flex items-center gap-2 text-blue-600">
              <Info className="h-4 w-4" />
              <span className="font-medium">Billing Address</span>
            </div>
            <p className="text-sm text-gray-600">
              This address will be used for all your invoices and
              billing-related documents. Make sure it matches your company's
              registered address if applicable.
            </p>
          </div>
        </CardContent>
      </Card>

      <Form {...form}>
        <form className="space-y-4">
          <FormField
            control={form.control}
            name="line1"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Address Line 1</FormLabel>
                <FormControl>
                  <Input className="border-0 bg-gray-50" {...field} disabled />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="line2"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Address Line 2</FormLabel>
                <FormControl>
                  <Input className="border-0 bg-gray-50" {...field} disabled />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="city"
            render={({ field }) => (
              <FormItem>
                <FormLabel>City</FormLabel>
                <FormControl>
                  <Input className="border-0 bg-gray-50" {...field} disabled />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="province"
            render={({ field }) => (
              <FormItem>
                <FormLabel>State/Province</FormLabel>
                <FormControl>
                  <Input className="border-0 bg-gray-50" {...field} disabled />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="country"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Country</FormLabel>
                <FormControl>
                  <Input className="border-0 bg-gray-50" {...field} disabled />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="postalCode"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Postal Code</FormLabel>
                <FormControl>
                  <Input className="border-0 bg-gray-50" {...field} disabled />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <div className="flex justify-end">
            <Dialog>
              <DialogTrigger asChild>
                <Button type="button" className="w-full">
                  {customer?.billing ? "Edit Billing" : "Add Billing"}
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>
                    {customer?.billing
                      ? "Edit Billing Information"
                      : "Add Billing Information"}
                  </DialogTitle>
                  <DialogDescription>
                    {customer?.billing
                      ? "Update your billing details below"
                      : "Add your billing information below"}
                  </DialogDescription>
                </DialogHeader>
                <Form {...form}>
                  <form onSubmit={onSubmit} className="space-y-4">
                    <AutocompleteAddress
                      onSelectedAddress={(selectedAddress) => {
                        if (selectedAddress) {
                          form.setValue(
                            "line1",
                            selectedAddress.addressComponents.line1
                          );
                          form.setValue(
                            "line2",
                            selectedAddress.addressComponents.line2 || ""
                          );
                          form.setValue(
                            "city",
                            selectedAddress.addressComponents.city
                          );
                          form.setValue(
                            "province",
                            selectedAddress.addressComponents.state
                          );
                          form.setValue(
                            "country",
                            selectedAddress.addressComponents.country
                          );
                          form.setValue(
                            "postalCode",
                            selectedAddress.addressComponents.postalCode || ""
                          );
                          form.setValue("location", selectedAddress.location);
                        }
                      }}
                      placeholder="Search address"
                    />

                    <div className="flex justify-end gap-4">
                      <DialogTrigger asChild>
                        <Button variant="outline">Cancel</Button>
                      </DialogTrigger>
                      <Button
                        type="submit"
                        disabled={
                          createBillingMutation.isPending ||
                          updateBillingMutation.isPending
                        }
                      >
                        {createBillingMutation.isPending ||
                        updateBillingMutation.isPending
                          ? "Saving..."
                          : "Save changes"}
                      </Button>
                    </div>
                  </form>
                </Form>
              </DialogContent>
            </Dialog>
          </div>
        </form>
      </Form>
    </div>
  );
}
